// src/lib/training/feedbackSubmit.ts
import { PLATFORM_DEFAULT_APP_DISPLAY_NAME } from "@/config/productDisplay";
import {
  readFeedbackQueue,
  writeFeedbackQueue,
  clearFeedbackQueue,
  getOrCreateDeviceId,
  type TrainingFeedbackEvent,
} from "./feedbackQueue";

const KEY_LAST_ATTEMPT = "bb-training-feedback-lastAttempt";

// Tune as desired
const MIN_SECONDS_BETWEEN_ATTEMPTS = 45;

export type TrainingFeedbackPayload = {
  source: "bennett_buddy_training";
  deviceId: string;
  submittedAt: number;
  appVersion: string;
  locationId?: string;
  locationName?: string;
  salespersonFirstName?: string;
  emailSummary?: string; // NEW

  // Union of tags from all events for easy workflow filtering
  bb_tags?: string[];

  events: TrainingFeedbackEvent[];
};

function now() {
  return Date.now();
}

function canAttempt(): boolean {
  try {
    const raw = localStorage.getItem(KEY_LAST_ATTEMPT);
    const last = raw ? Number(raw) : 0;
    return now() - last > MIN_SECONDS_BETWEEN_ATTEMPTS * 1000;
  } catch {
    return true;
  }
}

function markAttempt() {
  try {
    localStorage.setItem(KEY_LAST_ATTEMPT, String(now()));
  } catch {
    // ignore
  }
}

export async function submitTrainingFeedbackQueue(opts: {
  webhookUrl: string;
  appVersion: string;
  locationId?: string;
  locationName?: string;
  salespersonFirstName?: string;
}): Promise<{ ok: boolean; submitted: number; status?: number }> {
  const events = readFeedbackQueue();
  if (events.length === 0) return { ok: true, submitted: 0 };

  // If offline, bail quietly
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { ok: false, submitted: 0 };
  }

  if (!canAttempt()) {
    return { ok: false, submitted: 0 };
  }

  markAttempt();

  const deviceId = getOrCreateDeviceId();

  // Oldest-first is nicer for humans/logs
  const oldestFirst = [...events].reverse();
  const submittedEvents = oldestFirst.slice(0, 3);
  const first = oldestFirst[0];

  const unionTags = Array.from(
    new Set(
      submittedEvents.flatMap((e) => (Array.isArray(e.bb_tags) ? e.bb_tags : []))
    )
  );

  const br = "<br>";
  const emailSummary =
    `${PLATFORM_DEFAULT_APP_DISPLAY_NAME} feedback received.${br}${br}` +
    `Submitted At: ${now()}${br}` +
    `Device ID: ${deviceId}${br}` +
    `App Version: ${opts.appVersion}${br}` +
    `Event count: ${events.length}${br}${br}` +
    `Salesperson: ${opts.salespersonFirstName || "—"}${br}` +
    `Location: ${opts.locationName || "—"} (${opts.locationId || "—"})${br}${br}` +
    `--- Event (first item) ---${br}` +
    `Event ID: ${first?.id || "—"}${br}` +
    `Event Timestamp: ${first?.ts || "—"}${br}` +
    `Reason: ${first?.reason || "—"}${br}${br}` +
    `Thread: ${first?.threadId || "—"} (turn ${first?.threadTurn || "—"}, kind ${first?.queryKind || "—"})${br}${br}` +
    `Query:${br}${first?.query || "—"}${br}${br}` +
    `Answer Preview:${br}${first?.answerPreview || "—"}${br}${br}` +
    `Feedback / Notes:${br}${first?.feedbackText || "—"}${br}`;

  const payload: TrainingFeedbackPayload = {
    source: "bennett_buddy_training",
    deviceId,
    submittedAt: now(),
    appVersion: opts.appVersion,
    locationId: opts.locationId,
    locationName: opts.locationName,
    salespersonFirstName: opts.salespersonFirstName,
    emailSummary,
    bb_tags: unionTags.length ? unionTags : undefined,
    events: submittedEvents,
  };

  try {
    const res = await fetch(opts.webhookUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (res.ok) {
      const remainingOldestFirst = oldestFirst.slice(submittedEvents.length);
      if (remainingOldestFirst.length === 0) {
        clearFeedbackQueue();
      } else {
        // Queue is stored newest-first
        writeFeedbackQueue([...remainingOldestFirst].reverse());
      }
      return { ok: true, submitted: submittedEvents.length, status: res.status };
    }

    // Non-2xx: keep queue
    return { ok: false, submitted: 0, status: res.status };
  } catch {
    // Network/timeout: keep queue
    return { ok: false, submitted: 0 };
  }
}
